import React, { useState, useEffect, useMemo } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Gavel, Calendar, Users, Building2, FileText, Download,
  CheckCircle2, XCircle, Clock, Award, BarChart3, Sparkles, Plus,
  User, Settings, LogOut, Menu, X, ChevronDown, ChevronUp, Send,
  Trash2, Edit2, MoreVertical, DollarSign, AlertCircle, Bell, Save,
  BookmarkPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { PERMISSIONS, hasPermission } from '@/lib/permissions';

/* v2.5.1: format integer days back to nicest unit for display. */
const formatDuration = (days) => {
  const n = parseInt(days, 10);
  if (!n || n <= 0) return null;
  if (n % 30 === 0) return `${n / 30} ${n / 30 === 1 ? 'month' : 'months'}`;
  if (n % 7 === 0) return `${n / 7} ${n / 7 === 1 ? 'week' : 'weeks'}`;
  return `${n} ${n === 1 ? 'day' : 'days'}`;
};



function BidRequestDetail({ user, onLogout }) {
  const navigate = useNavigate();
  const { bidRequestId } = useParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Role-based permissions state
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  
  // Permission check helper
  const can = (permission) => {
    if (!isTeamMember) return true; // Owner has all permissions
    return hasPermission(teamRoles, permission);
  };
  
  // Data states
  const [bidRequest, setBidRequest] = useState(null);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialog states
  const [isAwardDialogOpen, setIsAwardDialogOpen] = useState(false);
  const [isExtractDialogOpen, setIsExtractDialogOpen] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  const [phaseSelections, setPhaseSelections] = useState([]);
  
  // Revision request states
  const [isRevisionDialogOpen, setIsRevisionDialogOpen] = useState(false);
  const [revisionMessage, setRevisionMessage] = useState('');
  const [revisionType, setRevisionType] = useState('general');
  const [revisionContractor, setRevisionContractor] = useState(null);
  const [revisionAmount, setRevisionAmount] = useState(0);
  const [isSendingRevision, setIsSendingRevision] = useState(false);
  
  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] = useState(false);
  
  // Reminder state
  const [sendingReminderId, setSendingReminderId] = useState(null);

  // Edit bid total (fix bids imported at $0)
  const [editTotalResponse, setEditTotalResponse] = useState(null);
  const [editTotalValue, setEditTotalValue] = useState('');
  const [savingTotal, setSavingTotal] = useState(false);

  const handleSaveResponseTotal = async () => {
    if (!editTotalResponse) return;
    setSavingTotal(true);
    try {
      const val = parseFloat(editTotalValue) || 0;
      await apiService.updateBidResponse(editTotalResponse.id, { total_amount: val }, user.id);
      toast.success('Bid total updated');
      setEditTotalResponse(null);
      loadData();
    } catch (e) {
      toast.error('Failed to update bid total');
    } finally {
      setSavingTotal(false);
    }
  };

  // Load custom templates from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('bidRevisionTemplates');
    if (saved) {
      try {
        setCustomTemplates(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved templates:', e);
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [bidRequestId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Check team membership for permissions
      if (user?.id) {
        try {
          const accessRes = await apiService.getTeamAccess(user.id);
          if (accessRes.data && accessRes.data.length > 0) {
            const access = accessRes.data[0];
            const roles = access.roles && access.roles.length > 0 
              ? access.roles 
              : [access.role || 'accountant'];
            setTeamRoles(roles);
            setIsTeamMember(true);
          } else {
            setIsTeamMember(false);
            setTeamRoles([]);
          }
        } catch (e) {
          setIsTeamMember(false);
          setTeamRoles([]);
        }
      }
      
      const [brRes, respRes] = await Promise.all([
        apiService.getBidRequest(bidRequestId, user.id),
        apiService.getBidResponses(bidRequestId, user.id)
      ]);
      setBidRequest(brRes.data);
      setResponses(respRes.data);
    } catch (error) {
      console.error('Failed to load bid request:', error);
      toast.error('Failed to load bid request');
      navigate('/project-hub');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!selectedResponseId || !revisionMessage.trim()) {
      toast.error('Please enter a revision message');
      return;
    }
    
    try {
      setIsSendingRevision(true);
      await apiService.requestBidRevision(user.id, {
        bid_response_id: selectedResponseId,
        message: revisionMessage,
        revision_type: revisionType
      });
      toast.success('Revision request sent to contractor!');
      setIsRevisionDialogOpen(false);
      setRevisionMessage('');
      setRevisionType('general');
      setSelectedResponseId(null);
      setRevisionContractor(null);
    } catch (error) {
      console.error('Failed to send revision request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send revision request');
    } finally {
      setIsSendingRevision(false);
    }
  };

  const handleAwardBid = async () => {
    if (!selectedResponseId) {
      toast.error('Please select a bid to award');
      return;
    }
    
    try {
      await apiService.awardBid(bidRequestId, {
        winning_response_id: selectedResponseId,
        user_id: user.id,
      });
      toast.success('Bid awarded successfully!');
      setIsAwardDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to award bid:', error);
      toast.error(error.response?.data?.detail || 'Failed to award bid');
    }
  };

  const handleExtractPhases = async () => {
    if (!selectedResponseId) return;
    
    const response = responses.find(r => r.id === selectedResponseId);
    if (!response) return;
    
    // Initialize phase selections from line items
    const selections = response.line_items.map((item, idx) => ({
      include: true,
      type: 'labor',
      line_item: item
    }));
    
    setPhaseSelections(selections);
    setIsExtractDialogOpen(true);
  };

  const handleCreatePhases = async () => {
    try {
      const result = await apiService.createPhasesFromBid(
        bidRequestId, 
        selectedResponseId, 
        user.id, 
        phaseSelections
      );
      toast.success(`Created ${result.data.phases?.length || 0} phases!`);
      setIsExtractDialogOpen(false);
      navigate(`/project/${bidRequest.project_id}`);
    } catch (error) {
      toast.error('Failed to create phases');
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await apiService.downloadDocument(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Save custom template
  const handleSaveTemplate = () => {
    if (!newTemplateName.trim() || !revisionMessage.trim()) {
      toast.error('Please enter both a template name and message');
      return;
    }
    
    const newTemplate = {
      id: Date.now(),
      name: newTemplateName.trim(),
      message: revisionMessage.trim()
    };
    
    const updated = [...customTemplates, newTemplate];
    setCustomTemplates(updated);
    localStorage.setItem('bidRevisionTemplates', JSON.stringify(updated));
    setNewTemplateName('');
    setIsSaveTemplateDialogOpen(false);
    toast.success('Template saved!');
  };

  // Delete custom template
  const handleDeleteTemplate = (templateId) => {
    const updated = customTemplates.filter(t => t.id !== templateId);
    setCustomTemplates(updated);
    localStorage.setItem('bidRevisionTemplates', JSON.stringify(updated));
    toast.success('Template deleted');
  };

  // Send reminder to contractor who hasn't submitted
  const handleSendReminder = async (contractor) => {
    try {
      setSendingReminderId(contractor.id);
      await apiService.sendBidReminder(bidRequestId, contractor.id, user.id);
      toast.success(`Reminder sent to ${contractor.company || contractor.name}!`);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminderId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { color: 'bg-green-100 text-green-700', icon: Clock },
      closed: { color: 'bg-gray-100 text-gray-700', icon: XCircle },
      awarded: { color: 'bg-blue-100 text-blue-700', icon: Award },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config.open;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const getResponseStatusBadge = (status) => {
    const config = {
      submitted: { color: 'bg-yellow-100 text-yellow-700', label: 'Submitted' },
      accepted: { color: 'bg-green-100 text-green-700', label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
      withdrawn: { color: 'bg-gray-100 text-gray-700', label: 'Withdrawn' }
    };
    const { color, label } = config[status] || config.submitted;
    return <Badge className={color}>{label}</Badge>;
  };

  // Find lowest bid for comparison highlighting
  const lowestTotal = useMemo(() => {
    if (responses.length === 0) return 0;
    return Math.min(...responses.map(r => r.total_amount));
  }, [responses]);

  // Get all unique line item descriptions for comparison table
  const allLineItems = useMemo(() => {
    const items = new Map();
    responses.forEach(resp => {
      resp.line_items?.forEach(item => {
        if (!items.has(item.description)) {
          items.set(item.description, item.description);
        }
      });
    });
    return Array.from(items.keys());
  }, [responses]);

  // Get price for a line item from a response
  const getLineItemPrice = (response, description) => {
    const item = response.line_items?.find(i => i.description === description);
    return item?.total || null;
  };

  // Find lowest price for a line item
  const getLowestPriceForItem = (description) => {
    const prices = responses
      .map(r => getLineItemPrice(r, description))
      .filter(p => p !== null);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!bidRequest) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p>Bid request not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/project-hub')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Gavel className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg hidden sm:block">Bid Request</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onLogout}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {getStatusBadge(bidRequest.status)}
                {bidRequest.trade_type && (
                  <Badge variant="outline" className="border-white/30 text-white">{bidRequest.trade_type}</Badge>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">{bidRequest.title}</h1>
              <div className="flex items-center gap-4 mt-2 text-blue-100">
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {bidRequest.project?.name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Due {formatDate(bidRequest.due_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {responses.length} bids received
                </span>
              </div>
            </div>
            
            {bidRequest.status === 'open' && responses.length > 0 && can(PERMISSIONS.AWARD_CONTRACTS) && (
              <Button 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => { setSelectedResponseId(null); setIsAwardDialogOpen(true); }}
                data-testid="award-bid-btn"
              >
                <Award className="w-4 h-4 mr-2" />
                Award Bid
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare" disabled={responses.length === 0}>
              Compare Bids ({responses.length})
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Scope & Line Items */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Scope of Work</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap text-slate-700">{bidRequest.scope_of_work}</p>
                  </CardContent>
                </Card>

                {bidRequest.line_items?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Line Items</CardTitle>
                      <CardDescription>Requested bid breakdown</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 font-medium">Description</th>
                              <th className="text-left py-2 px-3 font-medium">Unit</th>
                              <th className="text-right py-2 px-3 font-medium">Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bidRequest.line_items.map((item, idx) => (
                              <tr key={idx} className="border-b last:border-0">
                                <td className="py-2 px-3">{item.description}</td>
                                <td className="py-2 px-3 text-slate-500">{item.unit || '-'}</td>
                                <td className="py-2 px-3 text-right">{item.quantity || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Responses */}
                <Card>
                  <CardHeader>
                    <CardTitle>Bid Responses</CardTitle>
                    <CardDescription>{responses.length} contractor(s) submitted bids</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {responses.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>No bids received yet</p>
                        <p className="text-sm">Contractors will receive an email notification</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {responses.map(resp => (
                          <div 
                            key={resp.id} 
                            className={`border rounded-lg p-4 ${resp.status === 'accepted' ? 'border-green-300 bg-green-50' : ''}`}
                            data-testid={`response-card-${resp.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                {/* Contractor Logo */}
                                {resp.contractor?.company_logo ? (
                                  <img 
                                    src={resp.contractor.company_logo} 
                                    alt={resp.contractor.company || 'Company logo'} 
                                    className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0 border-2 border-slate-200">
                                    <span className="text-blue-600 font-semibold text-lg">
                                      {(resp.contractor?.company || resp.contractor?.name || 'C')[0].toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">{resp.contractor?.company}</h4>
                                    {getResponseStatusBadge(resp.status)}
                                    {resp.total_amount === lowestTotal && responses.length > 1 && (
                                      <Badge className="bg-green-100 text-green-700">Lowest</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500">{resp.contractor?.name}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-slate-900">
                                  ${resp.total_amount?.toLocaleString()}
                                </div>
                                {resp.estimated_days && (
                                  <p className="text-sm text-slate-500">{formatDuration(resp.estimated_days)}</p>
                                )}
                              </div>
                            </div>
                            
                            {resp.line_items?.length > 0 && (
                              <div className="mt-4 border-t pt-4">
                                <p className="text-sm font-medium mb-2">Line Item Breakdown:</p>
                                <div className="space-y-1">
                                  {resp.line_items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-sm">
                                      <span className="text-slate-600">{item.description}</span>
                                      <span className="font-medium">${item.total?.toLocaleString()}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* v2.5.5.5: contractor-provided per-stage day breakdown */}
                            {resp.stage_breakdown?.length > 0 && (
                              <div className="mt-4 border-t pt-4" data-testid={`stage-breakdown-${resp.id}`}>
                                <p className="text-sm font-medium mb-2 inline-flex items-center gap-2">
                                  Stage Breakdown
                                  <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                    Contractor-provided
                                  </span>
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {resp.stage_breakdown.map((stg, idx) => (
                                    <div key={idx} className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-center">
                                      <div className="text-[10px] uppercase font-bold tracking-wider text-blue-700">{stg.stage_name}</div>
                                      <div className="text-base font-bold text-slate-900 mt-0.5">{stg.days}d</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {resp.notes && (
                              <p className="mt-3 text-sm text-slate-600 italic">"{resp.notes}"</p>
                            )}
                            
                            {bidRequest.status === 'open' && (
                              <div className="mt-4 flex gap-2">
                                {can(PERMISSIONS.AWARD_CONTRACTS) && (
                                  <Button 
                                    size="sm"
                                    onClick={() => { setSelectedResponseId(resp.id); setIsAwardDialogOpen(true); }}
                                  >
                                    <Award className="w-4 h-4 mr-1" /> Award
                                  </Button>
                                )}
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { 
                                    setSelectedResponseId(resp.id); 
                                    setRevisionContractor(resp.contractor);
                                    setRevisionAmount(resp.total_amount);
                                    setIsRevisionDialogOpen(true); 
                                  }}
                                  data-testid={`request-revision-btn-${resp.id}`}
                                >
                                  <Edit2 className="w-4 h-4 mr-1" /> Request Revision
                                </Button>
                              </div>
                            )}
                            
                            {can(PERMISSIONS.AWARD_CONTRACTS) && (
                              <div className="mt-3">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-700 hover:bg-blue-50 px-2"
                                  data-testid={`edit-bid-total-${resp.id}`}
                                  onClick={() => { setEditTotalResponse(resp); setEditTotalValue(String(resp.total_amount || '')); }}
                                >
                                  <Edit2 className="w-4 h-4 mr-1" /> Edit total
                                </Button>
                              </div>
                            )}

                            {resp.status === 'accepted' && (
                              <div className="mt-4">
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setSelectedResponseId(resp.id); handleExtractPhases(); }}
                                  data-testid="extract-phases-btn"
                                >
                                  <Sparkles className="w-4 h-4 mr-1" /> Extract to Phases
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Contractors Invited */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Contractors Invited</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {bidRequest.contractors?.map(c => {
                        const hasResponded = responses.some(r => r.contractor_id === c.id);
                        return (
                          <div key={c.id} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasResponded ? 'bg-green-100' : 'bg-slate-100'}`}>
                              {hasResponded ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{c.company}</p>
                              <p className="text-xs text-slate-500">{hasResponded ? 'Submitted' : 'Pending'}</p>
                            </div>
                            {/* Reminder button for contractors who haven't submitted */}
                            {!hasResponded && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => handleSendReminder(c)}
                                disabled={sendingReminderId === c.id}
                                title="Send reminder email"
                                data-testid={`remind-btn-${c.id}`}
                              >
                                {sendingReminderId === c.id ? (
                                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                ) : (
                                  <Bell className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Attached Documents */}
                {bidRequest.documents?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Attached Documents</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {bidRequest.documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span className="text-sm truncate">{doc.file_name}</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {bidRequest.notes && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600">{bidRequest.notes}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare">
            <Card>
              <CardHeader>
                <CardTitle>Bid Comparison</CardTitle>
                <CardDescription>Side-by-side comparison of all submitted bids</CardDescription>
              </CardHeader>
              <CardContent>
                {responses.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No bids to compare</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-3 px-4 font-medium sticky left-0 bg-slate-50">Line Item</th>
                          {responses.map(resp => (
                            <th key={resp.id} className="text-center py-3 px-4 font-medium min-w-[150px]">
                              <div>{resp.contractor?.company}</div>
                              <div className="text-xs text-slate-500 font-normal">
                                Submitted {formatDate(resp.submitted_at)}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {allLineItems.map((description, idx) => {
                          const lowestPrice = getLowestPriceForItem(description);
                          return (
                            <tr key={idx} className="border-b">
                              <td className="py-3 px-4 font-medium sticky left-0 bg-white">{description}</td>
                              {responses.map(resp => {
                                const price = getLineItemPrice(resp, description);
                                const isLowest = price !== null && price === lowestPrice;
                                return (
                                  <td 
                                    key={resp.id} 
                                    className={`py-3 px-4 text-center ${isLowest ? 'bg-green-50 text-green-700 font-semibold' : ''}`}
                                  >
                                    {price !== null ? (
                                      <div>
                                        ${price.toLocaleString()}
                                        {isLowest && responses.length > 1 && (
                                          <span className="text-xs ml-1">↓</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        {/* Total Row */}
                        <tr className="bg-slate-100 font-semibold">
                          <td className="py-3 px-4 sticky left-0 bg-slate-100">TOTAL</td>
                          {responses.map(resp => (
                            <td 
                              key={resp.id} 
                              className={`py-3 px-4 text-center ${resp.total_amount === lowestTotal ? 'text-green-700' : ''}`}
                            >
                              ${resp.total_amount?.toLocaleString()}
                              {resp.total_amount === lowestTotal && responses.length > 1 && (
                                <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Lowest</Badge>
                              )}
                            </td>
                          ))}
                        </tr>
                        {/* Days Row */}
                        <tr className="bg-slate-50">
                          <td className="py-3 px-4 sticky left-0 bg-slate-50 text-slate-600">Est. Duration</td>
                          {responses.map(resp => (
                            <td key={resp.id} className="py-3 px-4 text-center text-slate-600">
                              {formatDuration(resp.estimated_days) || '-'}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Contractor Details */}
                {responses.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-medium mb-4">Contractor Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {responses.map(resp => (
                        <div 
                          key={resp.id} 
                          className={`border rounded-lg p-4 ${resp.total_amount === lowestTotal ? 'border-green-300 bg-green-50' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-semibold">{resp.contractor?.company}</h5>
                            {resp.total_amount === lowestTotal && (
                              <Badge className="bg-green-100 text-green-700">Lowest</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mb-3">{resp.contractor?.name}</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-500">W-9:</span>
                              <span className={resp.contractor?.w9_received ? 'text-green-600' : 'text-yellow-600'}>
                                {resp.contractor?.w9_received ? '✓ On file' : 'Pending'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Past Projects:</span>
                              <span>{resp.past_projects_count || 0} with you</span>
                            </div>
                          </div>
                          {bidRequest.status === 'open' && can(PERMISSIONS.AWARD_CONTRACTS) && (
                            <Button 
                              className="w-full mt-4" 
                              size="sm"
                              onClick={() => { setSelectedResponseId(resp.id); setIsAwardDialogOpen(true); }}
                            >
                              <Award className="w-4 h-4 mr-1" /> Award Bid
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Award Dialog */}
      {/* Edit bid total dialog (fix $0 imported bids) */}
      <Dialog open={!!editTotalResponse} onOpenChange={(o) => { if (!o) setEditTotalResponse(null); }}>
        <DialogContent data-testid="edit-bid-total-dialog">
          <DialogHeader>
            <DialogTitle>Edit bid total</DialogTitle>
            <DialogDescription>
              {editTotalResponse?.contractor_name || editTotalResponse?.contractor?.company_name || 'Contractor'} — set the correct total for this bid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="edit-bid-total-input">Total amount ($)</Label>
            <Input
              id="edit-bid-total-input"
              type="number"
              min="0"
              step="0.01"
              value={editTotalValue}
              onChange={(e) => setEditTotalValue(e.target.value)}
              data-testid="edit-bid-total-input"
              placeholder="e.g. 18140"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTotalResponse(null)}>Cancel</Button>
            <Button onClick={handleSaveResponseTotal} disabled={savingTotal} data-testid="save-bid-total-btn">
              {savingTotal ? 'Saving…' : 'Save total'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={isAwardDialogOpen} onOpenChange={setIsAwardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Award Bid</DialogTitle>
            <DialogDescription>Select the winning bid for this request</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {responses.map(resp => (
              <label 
                key={resp.id}
                className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 ${selectedResponseId === resp.id ? 'border-blue-500 bg-blue-50' : ''}`}
              >
                <input 
                  type="radio"
                  name="awardBid"
                  checked={selectedResponseId === resp.id}
                  onChange={() => setSelectedResponseId(resp.id)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-medium">{resp.contractor?.company}</span>
                    <span className="font-bold text-lg">${resp.total_amount?.toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-500">{resp.contractor?.name}</p>
                </div>
                {resp.total_amount === lowestTotal && (
                  <Badge className="bg-green-100 text-green-700">Lowest</Badge>
                )}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAwardDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAwardBid} disabled={!selectedResponseId} data-testid="confirm-award-btn">
              <Award className="w-4 h-4 mr-2" />
              Award Bid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extract Phases Dialog */}
      <Dialog open={isExtractDialogOpen} onOpenChange={setIsExtractDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              AI Bid Extraction
            </DialogTitle>
            <DialogDescription>Convert accepted bid into project phases</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">Bid Accepted!</span>
              </div>
              <p className="text-sm text-green-600 mt-1">
                {responses.find(r => r.id === selectedResponseId)?.contractor?.company} for $
                {responses.find(r => r.id === selectedResponseId)?.total_amount?.toLocaleString()}
              </p>
            </div>

            <h4 className="font-medium mb-3">Phases to Create</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left py-2 px-3 w-10">
                      <Checkbox 
                        checked={phaseSelections.every(s => s.include)}
                        onCheckedChange={(checked) => {
                          setPhaseSelections(phaseSelections.map(s => ({ ...s, include: !!checked })));
                        }}
                      />
                    </th>
                    <th className="text-left py-2 px-3">Phase Name</th>
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-right py-2 px-3">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {phaseSelections.map((sel, idx) => (
                    <tr key={idx} className={`border-b ${!sel.include ? 'opacity-50' : ''}`}>
                      <td className="py-2 px-3">
                        <Checkbox 
                          checked={sel.include}
                          onCheckedChange={(checked) => {
                            const updated = [...phaseSelections];
                            updated[idx].include = !!checked;
                            setPhaseSelections(updated);
                          }}
                        />
                      </td>
                      <td className="py-2 px-3">{sel.line_item?.description}</td>
                      <td className="py-2 px-3">
                        <select 
                          className="border rounded px-2 py-1 text-sm"
                          value={sel.type}
                          onChange={(e) => {
                            const updated = [...phaseSelections];
                            updated[idx].type = e.target.value;
                            setPhaseSelections(updated);
                          }}
                        >
                          <option value="labor">Labor</option>
                          <option value="material">Material</option>
                        </select>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        ${sel.line_item?.total?.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-semibold">
                    <td colSpan={3} className="py-2 px-3">
                      Total ({phaseSelections.filter(s => s.include).length} phases)
                    </td>
                    <td className="py-2 px-3 text-right text-green-600">
                      ${phaseSelections.filter(s => s.include).reduce((sum, s) => sum + (s.line_item?.total || 0), 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExtractDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreatePhases}
              disabled={!phaseSelections.some(s => s.include)}
              data-testid="create-phases-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Phases & Add to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Revision Dialog */}
      <Dialog open={isRevisionDialogOpen} onOpenChange={setIsRevisionDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-600" />
              Request Bid Revision
            </DialogTitle>
            <DialogDescription>
              Send a message to the contractor asking them to revise their bid.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Bid Info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{revisionContractor?.company || revisionContractor?.name}</p>
                  <p className="text-sm text-slate-500">{revisionContractor?.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Current Bid</p>
                  <p className="text-lg font-bold text-slate-900">${revisionAmount?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Revision Type */}
            <div>
              <Label>Revision Type</Label>
              <select 
                className="w-full mt-1.5 h-10 px-3 border border-slate-200 rounded-md text-sm"
                value={revisionType}
                onChange={(e) => setRevisionType(e.target.value)}
              >
                <option value="general">General Revision</option>
                <option value="price_adjustment">Price Adjustment</option>
                <option value="add_options">Add Options (e.g., with/without materials)</option>
                <option value="clarification">Clarification Needed</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <Label>Message to Contractor *</Label>
              <textarea
                className="w-full mt-1.5 p-3 border border-slate-200 rounded-md text-sm min-h-[120px]"
                placeholder="Please provide pricing for the following options:&#10;&#10;1. With materials included&#10;2. Labor only (we supply materials)"
                value={revisionMessage}
                onChange={(e) => setRevisionMessage(e.target.value)}
                data-testid="revision-message-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Be specific about what changes you need. The contractor will receive this message via email.
              </p>
            </div>

            {/* Quick Message Templates */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-slate-500">Quick Templates</Label>
                {revisionMessage.trim() && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-blue-600 hover:text-blue-700"
                    onClick={() => setIsSaveTemplateDialogOpen(true)}
                  >
                    <BookmarkPlus className="w-3 h-3 mr-1" />
                    Save as Template
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Built-in templates */}
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setRevisionMessage('Please provide pricing with materials included AND labor-only pricing (we would supply materials).')}
                >
                  With/Without Materials
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setRevisionMessage('Can you provide a more detailed breakdown of the line items in your bid?')}
                >
                  Detailed Breakdown
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setRevisionMessage('We need a lower price to stay within budget. Can you revise your bid or suggest alternatives?')}
                >
                  Budget Concern
                </Button>
                
                {/* Custom templates */}
                {customTemplates.map(template => (
                  <div key={template.id} className="relative group">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      className="pr-7 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      onClick={() => setRevisionMessage(template.message)}
                    >
                      {template.name}
                    </Button>
                    <button
                      type="button"
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      title="Delete template"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {customTemplates.length > 0 && (
                <p className="text-xs text-slate-400 mt-2">
                  Blue templates are your saved custom templates. Hover to delete.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRevisionDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRequestRevision}
              disabled={!revisionMessage.trim() || isSendingRevision}
              data-testid="send-revision-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSendingRevision ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Revision Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={isSaveTemplateDialogOpen} onOpenChange={setIsSaveTemplateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-blue-600" />
              Save Custom Template
            </DialogTitle>
            <DialogDescription>
              Save this message as a reusable template for future revision requests.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                className="mt-1.5"
                placeholder="e.g., Request Timeline, Scope Clarification..."
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                data-testid="template-name-input"
              />
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Message Preview</Label>
              <div className="mt-1.5 p-3 bg-slate-50 rounded-md text-sm text-slate-600 max-h-32 overflow-y-auto">
                {revisionMessage || 'No message to save'}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSaveTemplateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveTemplate}
              disabled={!newTemplateName.trim() || !revisionMessage.trim()}
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BidRequestDetail;
